import { Form, Input, Select, Row, Col, Spin } from 'antd';
import NSPDatePicker from '@components/ui/NSPDatepicker';
import NSPCheckbox from '@components/ui/NSPCheckbox';

const glValidationRules = [
  { pattern: /^[0-9.]+$/, message: 'Only numbers and periods are allowed' },
];

type Props = {
  onVendorBlur?: () => void;
  ippLookupLoading?: boolean;
};

const CoreInfoGeneralInfoSection = ({ onVendorBlur, ippLookupLoading }: Props) => {
  return (
    <>
      {/* Row 0 */}
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="Meter Number"
            name="meterNumber"
            data-testid="meter-number-field"
            rules={[{ required: true, message: 'Meter number is required' }]}
          >
            <Input disabled />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="Location"
            name="meterName"
            data-testid="location-field"
            rules={[{ required: true, message: 'Location is required' }]}
          >
            <Input />
          </Form.Item>
        </Col>
      </Row>

      {/* Row 1 */}
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="Vendor Name"
            name={['ipp', 'vendorName']}
            data-testid="vendor-name-field"
            rules={[{ required: true, message: 'Vendor Name is required' }]}
          >
            <Input
              onBlur={onVendorBlur}
              //   Always rendered — toggled via CSS visibility, not mount/unmount.
              // Switching between null and <Spin/> restructures the DOM and
              // causes the Input to lose focus (AntD FAQ warning).
              suffix={
                <Spin
                  size="small"
                  style={{ visibility: ippLookupLoading ? 'visible' : 'hidden' }}
                  data-testid="vendor-lookup-spinner"
                  data-loading={String(Boolean(ippLookupLoading))}
                />
              }
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="Energy"
            name="energy"
            data-testid="energy-field"
            rules={[{ required: true, message: 'Energy is required' }]}
          >
            <Select
              data-testid="energy-select"
              options={[
                { value: 'Wind', label: 'Wind' },
                { value: 'Solar', label: 'Solar' },
              ]}
            />
          </Form.Item>
        </Col>
      </Row>

      {/* Row 2 */}
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="Region"
            name="region"
            data-testid="region-field"
            rules={[{ required: true, message: 'Region is required' }]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="PO Line Number" name="poLineNumber" data-testid="po-line-number-field">
            <Input />
          </Form.Item>
        </Col>
      </Row>

      {/* Row 3 */}
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="Stat GL"
            name={['statGl', 'glNumber']}
            data-testid="stat-gl-field"
            rules={[...glValidationRules, { required: true, message: 'Stat GL is required' }]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="Dollar GL"
            name={['dollarGl', 'glNumber']}
            data-testid="dollar-gl-field"
            rules={[...glValidationRules, { required: true, message: 'Dollar GL is required' }]}
          >
            <Input />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={24}>
          <Form.Item
            label="Invoice to include meter read details?"
            name={['ipp', 'needDetailsWithInvoice']}
            data-testid="invoice-details-field"
          >
            <Select
              data-testid="invoice-select"
              options={[
                { value: 'Yes', label: 'Yes' },
                { value: 'No', label: 'No' },
              ]}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={24}>
          <Form.Item
            label="IPP Type?"
            name="meterType"
            data-testid="ipp-type-field"
            rules={[{ required: true, message: 'IPP Type is required' }]}
          >
            <Select
              data-testid="ipp-type-select"
              options={[
                { value: 'IPP', label: 'IPP' },
                { value: 'COMFIT', label: 'COMFIT' },
                { value: 'AGC Wind Farm', label: 'AGC Wind Farm' },
                { value: 'Community Solar', label: 'Community Solar' },
              ]}
            />
          </Form.Item>
        </Col>
      </Row>

      {/* Operational Status */}
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label="Operational Status" name="operationalStatus" data-testid="operational-status-field">
            <Select
              placeholder="Select status"
              options={[
                { value: 'Active', label: 'Active' },
                { value: 'Inactive', label: 'Inactive' },
              ]}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="Date of Status" name="operationalStatusDate" data-testid="status-date-field">
            <NSPDatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        noStyle
        shouldUpdate={(prevValues, currentValues) => prevValues.meterType !== currentValues.meterType}
      >
        {({ getFieldValue }) =>
          getFieldValue('meterType') === 'AGC Wind Farm' ? (
            <>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="PI Tag - AGC Mode / Arg Control Status"
                    name="piTagNameArgControlStatus"
                    data-testid="pi-tag-agc-mode-field"
                    rules={[{ required: true, message: 'PI Tag - AGC Mode / Arg Control Status is required.' }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="PI Tag - Megawatt Value"
                    name="piTagNameMegawattValue"
                    data-testid="pi-tag-megawatt-field"
                    rules={[{ required: true, message: 'PI Tag - Megawatt Value is required.' }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Loss Factor"
                    name="lossFactor"
                    data-testid="loss-factor-field"
                    rules={[
                      { required: true, message: 'Loss Factor is required.' },
                      { pattern: /^\d+(?:\.\d+)?$/, message: 'Loss Factor must be a numeric value.' },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
            </>
          ) : null
        }
      </Form.Item>

      <Form.Item name="applyHst" valuePropName="checked" initialValue={false} data-testid="apply-hst-field">
        <NSPCheckbox data-testid="apply-hst-checkbox">Apply HST to Invoice</NSPCheckbox>
      </Form.Item>

    </>
  );
};

export default CoreInfoGeneralInfoSection;